/**
 * DashboardEntityPOMCore - Base class for all entity Page Object Models
 *
 * Provides standard CRUD operations for dashboard entities:
 * - Navigation (list, create, edit, detail pages)
 * - Table interactions (search, filters, pagination, row actions)
 * - Form operations (fill fields, submit, cancel)
 * - API interceptor integration for deterministic waits
 * - Bulk actions
 * - Delete confirmation dialogs
 *
 * This is the CORE version. Themes should extend this class and implement
 * the abstract `cySelector` method from BasePOMCore.
 *
 * @example Theme extension:
 * ```ts
 * // theme/tests/cypress/src/core/DashboardEntityPOM.ts
 * import { DashboardEntityPOMCore } from '@nextsparkjs/testing/pom'
 * import { cySelector } from '../selectors'
 *
 * export abstract class DashboardEntityPOM extends DashboardEntityPOMCore {
 *   protected cySelector(path: string, replacements?: Record<string, string>): string {
 *     return cySelector(path, replacements)
 *   }
 * }
 * ```
 */

import { BasePOMCore, type Replacements } from './BasePOMCore'
import { ApiInterceptor } from '../helpers/ApiInterceptor'

export interface EntityConfig {
  slug: string
  singular?: string
  plural?: string
  tableName?: string
  fields?: string[]
  filters?: string[]
}

export abstract class DashboardEntityPOMCore extends BasePOMCore {
  protected slug: string
  protected entityConfig: EntityConfig
  protected _api: ApiInterceptor | null = null

  /**
   * Get the entity slug (public accessor)
   * Useful for building dynamic selectors and URLs in tests
   */
  get entitySlug(): string {
    return this.slug
  }

  constructor(entitySlugOrConfig: string | EntityConfig) {
    super()
    if (typeof entitySlugOrConfig === 'string') {
      this.slug = entitySlugOrConfig
      this.entityConfig = { slug: entitySlugOrConfig }
    } else {
      this.slug = entitySlugOrConfig.slug
      this.entityConfig = entitySlugOrConfig
    }
  }

  // ============================================
  // API INTERCEPTOR
  // ============================================

  /**
   * Get or create ApiInterceptor instance for this entity
   */
  get api(): ApiInterceptor {
    if (!this._api) {
      this._api = new ApiInterceptor(this.slug)
    }
    return this._api
  }

  /**
   * Setup API intercepts for all CRUD operations
   * Call this BEFORE navigation
   */
  setupApiIntercepts() {
    this.api.setupCrudIntercepts()
    return this
  }

  // ============================================
  // SELECTORS (uses cySelector from theme)
  // ============================================

  /**
   * Get all selectors for this entity, with placeholders replaced
   * Uses the abstract cySelector method which themes implement
   */
  get selectors() {
    const slug = this.slug

    return {
      // Page
      page: this.cySelector('entities.page.container', { slug }),

      // List - Table
      tableContainer: this.cySelector('entities.list.table.container', { slug }),
      table: this.cySelector('entities.list.table.element', { slug }),
      addButton: this.cySelector('entities.list.addButton', { slug }),
      selectAll: this.cySelector('entities.list.table.selectAll', { slug }),
      selectionCount: this.cySelector('entities.list.selectionCount', { slug }),
      row: (id: string) => this.cySelector('entities.list.table.row.element', { slug, id }),
      rowSelect: (id: string) => this.cySelector('entities.list.table.row.checkbox', { slug, id }),
      rowMenu: (id: string) => this.cySelector('entities.list.table.row.menu', { slug, id }),
      rowAction: (action: string, id: string) => this.cySelector('entities.list.table.row.action', { slug, action, id }),
      cell: (name: string, id: string) => this.cySelector('entities.list.table.cell.element', { slug, name, id }),
      rowGeneric: `[data-cy^="${slug}-row-"]`,

      // List - Search
      search: this.cySelector('entities.list.search.input', { slug }),
      searchContainer: this.cySelector('entities.list.search.container', { slug }),
      searchClear: this.cySelector('entities.list.search.clear', { slug }),

      // List - Pagination
      pagination: this.cySelector('entities.list.pagination.container', { slug }),
      pageSize: this.cySelector('entities.list.pagination.pageSize', { slug }),
      pageSizeOption: (size: string) => this.cySelector('entities.list.pagination.pageSizeOption', { slug, size }),
      pageInfo: this.cySelector('entities.list.pagination.info', { slug }),
      pageFirst: this.cySelector('entities.list.pagination.first', { slug }),
      pagePrev: this.cySelector('entities.list.pagination.prev', { slug }),
      pageNext: this.cySelector('entities.list.pagination.next', { slug }),
      pageLast: this.cySelector('entities.list.pagination.last', { slug }),

      // List - Filters
      filter: (field: string) => this.cySelector('entities.list.filters.container', { slug, field }),
      filterTrigger: (field: string) => this.cySelector('entities.list.filters.trigger', { slug, field }),
      filterContent: (field: string) => this.cySelector('entities.list.filters.content', { slug, field }),
      filterOption: (field: string, value: string) => this.cySelector('entities.list.filters.option', { slug, field, value }),
      filterBadge: (field: string, value: string) => this.cySelector('entities.list.filters.badge', { slug, field, value }),
      filterRemoveBadge: (field: string, value: string) => this.cySelector('entities.list.filters.removeBadge', { slug, field, value }),
      filterClearAll: (field: string) => this.cySelector('entities.list.filters.clearAll', { slug, field }),

      // List - Bulk actions
      bulkBar: this.cySelector('entities.list.bulk.bar', { slug }),
      bulkCount: this.cySelector('entities.list.bulk.count', { slug }),
      bulkSelectAll: this.cySelector('entities.list.bulk.selectAll', { slug }),
      bulkStatus: this.cySelector('entities.list.bulk.statusButton', { slug }),
      bulkDelete: this.cySelector('entities.list.bulk.deleteButton', { slug }),
      bulkClear: this.cySelector('entities.list.bulk.clearButton', { slug }),

      // List - Bulk status dialog
      bulkStatusDialog: this.cySelector('entities.list.bulk.statusDialog', { slug }),
      bulkStatusSelect: this.cySelector('entities.list.bulk.statusSelect', { slug }),
      bulkStatusOption: (value: string) => this.cySelector('entities.list.bulk.statusOption', { slug, value }),
      bulkStatusCancel: this.cySelector('entities.list.bulk.statusCancel', { slug }),
      bulkStatusConfirm: this.cySelector('entities.list.bulk.statusConfirm', { slug }),

      // List - Bulk delete dialog
      bulkDeleteDialog: this.cySelector('entities.list.bulk.deleteDialog', { slug }),
      bulkDeleteCancel: this.cySelector('entities.list.bulk.deleteCancel', { slug }),
      bulkDeleteConfirm: this.cySelector('entities.list.bulk.deleteConfirm', { slug }),

      // List - Confirm dialogs (for row actions)
      confirmDialog: this.cySelector('entities.list.confirm.dialog', { slug }),
      confirmCancel: this.cySelector('entities.list.confirm.cancel', { slug }),
      confirmAction: this.cySelector('entities.list.confirm.action', { slug }),

      // Header (detail pages) - modes: view, edit, create
      viewHeader: this.cySelector('entities.header.container', { slug, mode: 'view' }),
      editHeader: this.cySelector('entities.header.container', { slug, mode: 'edit' }),
      createHeader: this.cySelector('entities.header.container', { slug, mode: 'create' }),
      backButton: this.cySelector('entities.header.backButton', { slug }),
      editButton: this.cySelector('entities.header.editButton', { slug }),
      deleteButton: this.cySelector('entities.header.deleteButton', { slug }),
      title: this.cySelector('entities.header.title', { slug }),

      // Header - Delete confirmation
      deleteDialog: this.cySelector('entities.header.deleteDialog', { slug }),
      deleteCancel: this.cySelector('entities.header.deleteCancel', { slug }),
      deleteConfirm: this.cySelector('entities.header.deleteConfirm', { slug }),

      // Detail view
      detail: this.cySelector('entities.detail.container', { slug }),

      // Form
      form: this.cySelector('entities.form.container', { slug }),
      field: (name: string) => this.cySelector('entities.form.field', { slug, name }),
      submitButton: this.cySelector('entities.form.submitButton', { slug }),

      // Parent delete confirmation (EntityDetailWrapper - generic, no slug)
      parentDeleteConfirm: '[data-cy="confirm-delete"]',
      parentDeleteCancel: '[data-cy="cancel-delete"]',

      // Row action selectors (generic patterns for checking existence)
      rowActionEditGeneric: `[data-cy^="${slug}-action-edit-"]`,
      rowActionDeleteGeneric: `[data-cy^="${slug}-action-delete-"]`,
    }
  }

  // ============================================
  // NAVIGATION
  // ============================================

  /**
   * Navigate to entity list page
   */
  visitList() {
    cy.visit(`/dashboard/${this.slug}`)
    return this
  }

  /**
   * Navigate to create page
   */
  visitCreate() {
    cy.visit(`/dashboard/${this.slug}/create`)
    return this
  }

  /**
   * Navigate to edit page for specific entity
   */
  visitEdit(id: string) {
    cy.visit(`/dashboard/${this.slug}/${id}/edit`)
    return this
  }

  /**
   * Navigate to detail/view page for specific entity
   */
  visitDetail(id: string) {
    cy.visit(`/dashboard/${this.slug}/${id}`)
    return this
  }

  // ============================================
  // API-AWARE NAVIGATION
  // ============================================

  /**
   * Navigate to list and wait for API response
   */
  visitListWithApiWait() {
    this.setupApiIntercepts()
    this.visitList()
    this.api.waitForList()
    return this
  }

  /**
   * Navigate to edit page and wait for form to be visible
   */
  visitEditWithApiWait(id: string) {
    this.setupApiIntercepts()
    this.visitEdit(id)
    this.waitForForm()
    return this
  }

  /**
   * Navigate to detail page and wait for content
   */
  visitDetailWithApiWait(id: string) {
    this.setupApiIntercepts()
    this.visitDetail(id)
    this.waitForDetail()
    return this
  }

  // ============================================
  // WAITS
  // ============================================

  /**
   * Wait for list page to be fully loaded
   */
  waitForList() {
    cy.url().should('include', `/dashboard/${this.slug}`)
    cy.get(this.selectors.tableContainer, { timeout: 15000 }).should('be.visible')
    return this
  }

  /**
   * Wait for form to be visible
   */
  waitForForm() {
    cy.get(this.selectors.form, { timeout: 15000 }).should('be.visible')
    return this
  }

  /**
   * Wait for detail page to be loaded
   */
  waitForDetail() {
    cy.url().should('match', new RegExp(`/dashboard/${this.slug}/[a-z0-9-]+$`))
    cy.get(this.selectors.editButton, { timeout: 15000 }).should('be.visible')
    return this
  }

  // ============================================
  // TABLE ACTIONS
  // ============================================

  /**
   * Click the Add/Create button
   */
  clickAdd() {
    cy.get(this.selectors.addButton).click()
    return this
  }

  /**
   * Type in the search input
   */
  search(term: string) {
    cy.get(this.selectors.search).clear().type(term)
    return this
  }

  /**
   * Clear the search input
   */
  clearSearch() {
    cy.get(this.selectors.searchClear).click()
    return this
  }

  /**
   * Click a specific row by ID
   */
  clickRow(id: string) {
    cy.get(this.selectors.row(id)).click()
    return this
  }

  /**
   * Find and click a row containing specific text
   */
  clickRowByText(text: string) {
    cy.contains(this.selectors.rowGeneric, text).click()
    return this
  }

  /**
   * Select a row checkbox
   */
  selectRow(id: string) {
    cy.get(this.selectors.rowSelect(id)).click()
    return this
  }

  /**
   * Open the row menu (three dots)
   */
  openRowMenu(id: string) {
    cy.get(this.selectors.rowMenu(id)).click()
    return this
  }

  /**
   * Click an action in the row menu
   */
  clickRowAction(action: string, id: string) {
    cy.get(this.selectors.rowAction(action, id)).click()
    return this
  }

  // ============================================
  // FILTERS
  // ============================================

  /**
   * Open a filter dropdown
   */
  openFilter(field: string) {
    cy.get(this.selectors.filterTrigger(field)).click()
    return this
  }

  /**
   * Select a filter option
   */
  selectFilterOption(field: string, value: string) {
    cy.get(this.selectors.filterOption(field, value)).click()
    return this
  }

  /**
   * Open filter and select option (convenience method)
   */
  selectFilter(field: string, value: string) {
    this.openFilter(field)
    this.selectFilterOption(field, value)
    return this
  }

  /**
   * Clear all selected options for a specific filter
   * NOTE: Clear button only appears when >1 option is selected
   */
  clearFilter(field: string) {
    cy.get(this.selectors.filterClearAll(field)).click()
    return this
  }

  // ============================================
  // PAGINATION
  // ============================================

  /**
   * Go to next page
   */
  nextPage() {
    cy.get(this.selectors.pageNext).click()
    return this
  }

  /**
   * Go to previous page
   */
  prevPage() {
    cy.get(this.selectors.pagePrev).click()
    return this
  }

  /**
   * Go to first page
   */
  firstPage() {
    cy.get(this.selectors.pageFirst).click()
    return this
  }

  /**
   * Go to last page
   */
  lastPage() {
    cy.get(this.selectors.pageLast).click()
    return this
  }

  /**
   * Change page size
   */
  setPageSize(size: string) {
    cy.get(this.selectors.pageSize).click()
    cy.get(this.selectors.pageSizeOption(size)).click()
    return this
  }

  // ============================================
  // FORM ACTIONS
  // ============================================

  /**
   * Fill a text input field
   */
  fillTextField(name: string, value: string) {
    cy.get(this.selectors.field(name)).find('input').clear().type(value)
    return this
  }

  /**
   * Fill a textarea field
   */
  fillTextarea(name: string, value: string) {
    cy.get(this.selectors.field(name)).find('textarea').clear().type(value)
    return this
  }

  /**
   * Select an option in a combobox/select field
   */
  selectOption(name: string, value: string) {
    cy.get(this.selectors.field(name)).find('[role="combobox"]').click()
    cy.get(`[data-cy="${this.slug}-field-${name}-option-${value}"]`).click()
    return this
  }

  /**
   * Submit the form
   */
  submitForm() {
    cy.get(this.selectors.submitButton).click()
    return this
  }

  // ============================================
  // HEADER/DETAIL ACTIONS
  // ============================================

  /**
   * Click back button
   */
  clickBack() {
    cy.get(this.selectors.backButton).click()
    return this
  }

  /**
   * Click edit button
   */
  clickEdit() {
    cy.get(this.selectors.editButton).click()
    return this
  }

  /**
   * Click delete button
   */
  clickDelete() {
    cy.get(this.selectors.deleteButton).click()
    return this
  }

  /**
   * Confirm delete in dialog
   */
  confirmDelete() {
    cy.get(this.selectors.deleteConfirm).click()
    return this
  }

  /**
   * Cancel delete in dialog
   */
  cancelDelete() {
    cy.get(this.selectors.deleteCancel).click()
    return this
  }

  // ============================================
  // BULK ACTIONS
  // ============================================

  /**
   * Select all items using table header checkbox
   */
  selectAll() {
    cy.get(this.selectors.selectAll).click()
    return this
  }

  /**
   * Click bulk delete button
   */
  bulkDelete() {
    cy.get(this.selectors.bulkDelete).click()
    return this
  }

  /**
   * Confirm bulk delete
   */
  confirmBulkDelete() {
    cy.get(this.selectors.bulkDeleteConfirm).click()
    return this
  }

  /**
   * Cancel bulk delete
   */
  cancelBulkDelete() {
    cy.get(this.selectors.bulkDeleteCancel).click()
    return this
  }

  /**
   * Click bulk status button
   */
  bulkChangeStatus() {
    cy.get(this.selectors.bulkStatus).click()
    return this
  }

  /**
   * Select status in bulk status dialog
   */
  selectBulkStatus(value: string) {
    cy.get(this.selectors.bulkStatusSelect).click()
    cy.get(this.selectors.bulkStatusOption(value)).click()
    return this
  }

  /**
   * Confirm bulk status change
   */
  confirmBulkStatus() {
    cy.get(this.selectors.bulkStatusConfirm).click()
    return this
  }

  /**
   * Clear selection
   */
  clearSelection() {
    cy.get(this.selectors.bulkClear).click()
    return this
  }

  // ============================================
  // ASSERTIONS
  // ============================================

  /**
   * Assert text is visible in the list
   */
  assertInList(text: string) {
    cy.contains(text).should('be.visible')
    return this
  }

  /**
   * Assert text is not in the list
   */
  assertNotInList(text: string) {
    cy.contains(text).should('not.exist')
    return this
  }

  /**
   * Assert table is visible
   */
  assertTableVisible() {
    cy.get(this.selectors.table).should('be.visible')
    return this
  }

  /**
   * Assert form is visible
   */
  assertFormVisible() {
    cy.get(this.selectors.form).should('be.visible')
    return this
  }

  /**
   * Assert page title contains text
   */
  assertPageTitle(expected: string) {
    cy.get(this.selectors.title).should('contain.text', expected)
    return this
  }

  /**
   * Assert row exists
   */
  assertRowExists(id: string) {
    cy.get(this.selectors.row(id)).should('exist')
    return this
  }

  /**
   * Assert row does not exist
   */
  assertRowNotExists(id: string) {
    cy.get(this.selectors.row(id)).should('not.exist')
    return this
  }

  /**
   * Assert selection count
   */
  assertSelectionCount(count: number) {
    cy.get(this.selectors.selectionCount).should('contain.text', count.toString())
    return this
  }

  /**
   * Assert bulk bar is visible
   */
  assertBulkBarVisible() {
    cy.get(this.selectors.bulkBar).should('be.visible')
    return this
  }

  /**
   * Assert bulk bar is hidden
   */
  assertBulkBarHidden() {
    cy.get(this.selectors.bulkBar).should('not.be.visible')
    return this
  }
}

export default DashboardEntityPOMCore
